import { useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/lib/api";
import { FUNNELS } from "@/lib/funnels";
import { getSource, trackFunnel } from "@/lib/tracking";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/sonner";
import { ArrowLeft, ArrowRight, Check, Loader2, ShieldCheck } from "lucide-react";
import { FunnelThankYou } from "@/components/funnel/FunnelThankYou";

const QuestionStep = ({ seg, qKey, value, onPick }) => {
  const { t } = useTranslation();
  const opts = t(`funnel.${seg}.questions.${qKey}.o`, { returnObjects: true });
  return (
    <div>
      <h3 className="font-heading text-2xl font-extrabold text-slate-900 sm:text-3xl">{t(`funnel.${seg}.questions.${qKey}.q`)}</h3>
      <div className="mt-6 grid gap-3 sm:grid-cols-2">
        {Object.entries(opts).map(([k, label]) => (
          <button
            key={k}
            type="button"
            onClick={() => onPick(k)}
            data-testid={`funnel-option-${qKey}-${k}`}
            className={`flex items-center justify-between gap-3 rounded-2xl border-2 px-5 py-4 text-left text-base font-semibold transition-colors ${value === k ? "border-orange-500 bg-orange-50 text-orange-900" : "border-slate-200 bg-white text-slate-800 hover:border-orange-300 hover:bg-orange-50/50"}`}
          >
            {label}
            {value === k && <Check className="h-5 w-5 shrink-0 text-orange-600" />}
          </button>
        ))}
      </div>
    </div>
  );
};

const ContactStep = ({ seg, form, setForm }) => {
  const { t } = useTranslation();
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const field = (k, label, props = {}) => (
    <div>
      <Label htmlFor={`f-${k}`} className="text-sm font-semibold">{label}</Label>
      <Input id={`f-${k}`} value={form[k]} onChange={set(k)} className="mt-1.5 h-12 text-base" data-testid={`funnel-input-${k}`} {...props} />
    </div>
  );
  return (
    <div>
      <h3 className="font-heading text-2xl font-extrabold text-slate-900 sm:text-3xl">{t("funnel.common.contactTitle")}</h3>
      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        {field("name", t("funnel.common.name"), { required: true, minLength: 2 })}
        {field("phone", t("funnel.common.phone"), { required: true, type: "tel", minLength: 7, maxLength: 20 })}
        {field("email", t("funnel.common.email"), { required: true, type: "email" })}
        {field("city", t("funnel.common.city"))}
        <div className="sm:col-span-2">{field("organisation", t(`funnel.${seg}.orgLabel`))}</div>
      </div>
      <input type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} className="hidden" aria-hidden="true" />
      <p className="mt-4 flex items-center gap-2 text-sm text-slate-500"><ShieldCheck className="h-4 w-4 text-emerald-600" /> {t("funnel.common.privacy")}</p>
    </div>
  );
};

export const QuizFunnel = ({ seg }) => {
  const { t, i18n } = useTranslation();
  const cfg = FUNNELS[seg];
  const total = cfg.questions.length + 1;
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [form, setForm] = useState({ name: "", email: "", phone: "", city: "", organisation: "", website: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const pick = (k) => (v) => {
    const cur = step;
    if (!Object.keys(answers).length) trackFunnel(seg, "quiz_start");
    setAnswers({ ...answers, [k]: v });
    setTimeout(() => setStep((s) => (s === cur ? Math.min(s + 1, total - 1) : s)), 220);
  };
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/funnel/${seg}`, { ...form, answers, source: getSource(), lang: i18n.language?.slice(0, 2) || "en" });
      setDone({ temperature: data.temperature, name: form.name.split(" ")[0] });
    } catch {
      toast.error(t("funnel.common.error"));
    } finally {
      setBusy(false);
    }
  };

  if (done) return <FunnelThankYou seg={seg} {...done} />;
  const qKey = cfg.questions[step];
  const isContact = step === total - 1;
  return (
    <form onSubmit={submit} className="rounded-3xl border border-slate-200 bg-white p-6 shadow-xl shadow-orange-500/5 sm:p-10" data-testid={`funnel-quiz-${seg}`}>
      <div className="flex items-center justify-between text-sm font-bold text-slate-500">
        <span data-testid="funnel-step-label">{t("funnel.common.step", { n: step + 1, total })}</span>
        <span className="text-emerald-700">{t("funnel.common.takes")}</span>
      </div>
      <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-100">
        <motion.div className={`h-full rounded-full bg-gradient-to-r ${cfg.accent}`} animate={{ width: `${((step + 1) / total) * 100}%` }} transition={{ duration: 0.4 }} />
      </div>
      <AnimatePresence mode="wait">
        <motion.div key={step} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.25 }} className="mt-8">
          {isContact ? <ContactStep seg={seg} form={form} setForm={setForm} /> : <QuestionStep seg={seg} qKey={qKey} value={answers[qKey]} onPick={pick(qKey)} />}
        </motion.div>
      </AnimatePresence>
      <div className="mt-8 flex items-center justify-between gap-3">
        <Button type="button" variant="ghost" disabled={step === 0} onClick={() => setStep(step - 1)} data-testid="funnel-back">
          <ArrowLeft className="mr-1.5 h-4 w-4" /> {t("funnel.common.back")}
        </Button>
        {isContact ? (
          <Button type="submit" disabled={busy} className={`h-12 rounded-full bg-gradient-to-r ${cfg.accent} px-8 text-base font-bold text-white shadow-lg`} data-testid="funnel-submit">
            {busy ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t("funnel.common.submitting")}</> : <>{t("funnel.common.submit")} <ArrowRight className="ml-2 h-4 w-4" /></>}
          </Button>
        ) : (
          <Button type="button" disabled={!answers[qKey]} onClick={() => setStep(step + 1)} variant="outline" className="rounded-full" data-testid="funnel-next">
            {t("funnel.common.next")} <ArrowRight className="ml-1.5 h-4 w-4" />
          </Button>
        )}
      </div>
    </form>
  );
};
